import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { betsApi, type PlaceMultiBody } from '@/api/endpoints';
import type { Bet } from '@/types/api';
import { ME_QUERY_KEY } from './useMe';
import { randomUUID } from '@/utils/uuid';

export function useBets(status?: Bet['status']) {
  return useQuery({
    queryKey: ['bets', status ?? 'all'],
    queryFn: () => betsApi.list({ limit: 50, status }),
    staleTime: 15_000,
  });
}

export function usePlaceBet() {
  const qc = useQueryClient();
  return useMutation({
    /* quoteId is REQUIRED by POST /bets (migration 0036 — the server owns the
       price). decimalOdds is a CLAIM the server checks against the stored quote,
       never the price struck; omitting quoteId is a 422 quote_missing, not a
       fallback to the body odds. It is non-optional here for the same reason it
       is non-optional on the wire: an optional field is the one a caller forgets. */
    mutationFn: (vars: {
      marketId: string;
      selectionId: string;
      quoteId: string;
      decimalOdds: number;
      stakeCents: number;
    }) =>
      betsApi.place({
        ...vars,
        clientBetUuid: randomUUID(),
      }),
    onSuccess: () => {
      // Bets list + balances both change on placement.
      void qc.invalidateQueries({ queryKey: ['bets'] });
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}

/** Straight multi (0049): same cache effects as a single; the ticket uuid is minted here. */
export function usePlaceMulti() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: Omit<PlaceMultiBody, 'clientBetUuid'>) =>
      betsApi.placeMulti({ ...vars, clientBetUuid: randomUUID() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bets'] });
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}
